import { EnumTipoOnereDTOTipoVincolo } from '@framework/api-client/api/inquiries/schemas/enumTipoOnereDTOTipoVincolo.ts';
import { type PostCompilazioneDTOInviato } from '@framework/api-client/api/inquiries/schemas/postCompilazioneDTOInviato.ts';
import {
  getGetFrontRichiesteIdQueryKey,
  useGetFrontRichiesteIdPagamenti,
} from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { EnumRichiestaDTOStato } from '@framework/api-client/api/inquiries-compile/schemas/enumRichiestaDTOStato.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { SemanticTagLogic } from '@framework/common-web/schema/semantic-tags/SemanticTagLogic.ts';
import { StyledHtmlText } from '@framework/engine/inputs/rich-text/style/StyledHtmlText.tsx';
import { SubmissionBurdenLogic } from '@framework/frontoffice-submission/burden/SubmissionBurdenLogic.ts';
import { PaymentsTable } from '@framework/frontoffice-submission/payments/PaymentsTable.tsx';
import { QuoteActionRequiredMessages } from '@framework/frontoffice-submission/payments/quote/QuoteActionRequiredMessages.tsx';
import { getValidPayments } from '@framework/frontoffice-submission/payments/utils/getValidPayments.ts';
import { SubmissionLogic } from '@framework/frontoffice-submission/submission/SubmissionLogic.ts';
import { ISODate } from '@framework/ui/components/date/ISODate.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { Alert, Divider, Flex, Result, Space, Typography } from 'antd';
import DOMPurify from 'dompurify';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInterval } from 'usehooks-ts';
import { queryClient } from '../../../core/network/queryClient.ts';
import { ConfigurableReceiptField } from './ConfigurableReceiptField.tsx';
import { computeShowAlert } from './computeShowAlert.ts';
import {
  ReceiptInstanceNumberField,
  ReceiptProcedureNumberField,
  ReceiptStatusField,
  ReceiptSubmissionDateField,
} from './receiptFieldComponents.tsx';
import { receiptVisibleFields } from './receiptVisibleFields.ts';

const { Text, Title } = Typography;

const SECONDS_30 = 30 * 1000;

export interface ISubmissionSentTabReceiptProps {
  active?: boolean;
  sentLayout?: PostCompilazioneDTOInviato | undefined;
  submission?: RichiestaDTO | undefined;
}

/**
 * Tab della ricevuta di invio richiesta.
 */
export function SubmissionSentTabReceipt(
  props: ISubmissionSentTabReceiptProps,
) {
  const { active, sentLayout, submission } = props;
  const { t } = useTranslation();
  // Campi configurati via RECEIPT_VISIBLE_FIELDS: la modalità custom si attiva
  // solo se la CSV contiene almeno un identificatore valido, altrimenti
  // (env assente, vuota o malformata) resta il comportamento di default.
  const hasCustomReceiptVisibleFields = receiptVisibleFields.length > 0;

  const paymentsQuery = useGetFrontRichiesteIdPagamenti(submission!.id!, {
    query: {
      keepPreviousData: true,
      // retryDelay: 5000,
      refetchInterval: (pagamenti, query) => {
        if (!pagamenti) {
          // query.options.retry = false;
          return false;
        }
        // query.options.retry = true;
        return getValidPayments(pagamenti).some(
          p => p.codStatoPagamento === 'PENDENTE',
        )
          ? 5000
          : false;
      },
    },
  });

  const payments = useMemo(
    () => getValidPayments(paymentsQuery.data),
    [paymentsQuery.data],
  );

  // Intervallo per il polling per aggiornare lo stato della richiesta
  // in attesa che venga protocollata. Aggiorno la richiesta finché non
  // è nello stato "RICEVUTA"
  useInterval(
    () => {
      if (!submission?.id) return;
      queryClient.invalidateQueries(
        getGetFrontRichiesteIdQueryKey(submission.id),
      );
    },
    submission?.stato !== EnumRichiestaDTOStato.RICEVUTA ? SECONDS_30 : null,
  );

  // Indica se devo o meno mostrare l'alert di richiesta inviata con successo
  const [showAlert, setShowAlert] = React.useState<boolean>(
    computeShowAlert(submission),
  );

  // Ricalcolo l'alert ogni 30 secondi
  useInterval(() => {
    setShowAlert(computeShowAlert(submission));
  }, SECONDS_30);

  const submissionCodes = SubmissionLogic.getCodes(submission);

  const semanticFields = useMemo(
    () =>
      // prendiamo tutti i campi semantici visualizzabili e li ordiniamo per titolo
      submission?.campiSemantici
        ?.filter(field => SemanticTagLogic.isVisible(field.tagSemantico))
        ?.sort(
          (t1, t2) =>
            (t1.tagSemantico?.titolo ?? '').localeCompare(
              t2.tagSemantico?.titolo ?? '',
            ) ?? 0,
        ) ?? [],
    [submission?.campiSemantici],
  );

  if (!active) return null;

  return (
    <div>
      {showAlert && (
        <>
          <Result
            status="success"
            title={
              sentLayout?.messaggio ??
              t(
                'submission.richiesta-inviata-con-successo',
                'Richiesta inviata con successo!',
              )
            }
            subTitle={
              sentLayout?.testo ? (
                <StyledHtmlText
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitize is safe
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(sentLayout?.testo),
                  }}
                />
              ) : (
                t(
                  'submission.richiesta-inviata-correttamente-da-prendere-in-carico',
                  'La richiesta è stata inviata correttamente e sarà presa in carico a breve.',
                )
              )
            }
          />
          <Divider />
        </>
      )}
      {SubmissionBurdenLogic.hasPendingQuotes(submission?.statoPreventivi) && (
        <Alert
          style={{ marginBottom: '24px' }}
          showIcon
          type="warning"
          message={<QuoteActionRequiredMessages submission={submission} />}
          description={t(
            'submission.vai-ai-pagamenti',
            'Vai alla sezione "Pagamenti" per visualizzare e gestire il preventivo',
          )}
        />
      )}
      <Flex vertical gap={6}>
        {hasCustomReceiptVisibleFields ? (
          receiptVisibleFields.map(field => (
            <ConfigurableReceiptField
              key={field}
              field={field}
              receiptSubmission={submission}
              submissionCodes={submissionCodes}
            />
          ))
        ) : (
          <ReceiptStatusField receiptSubmission={submission} />
        )}
        {submission?.oggetto && (
          <Space>
            <Text style={{ fontWeight: 'normal' }}>
              {t('submission.oggetto-richiesta', 'Oggetto:')}{' '}
            </Text>
            <Text strong>{submission?.oggetto}</Text>
          </Space>
        )}
        {submissionCodes.submissionCode && (
          <Space>
            <Text>{t('submission.codice-richiesta', 'Codice richiesta:')}</Text>
            <Text strong>{submissionCodes.submissionCode}</Text>
          </Space>
        )}
        {!hasCustomReceiptVisibleFields && (
          <>
            <ReceiptProcedureNumberField submissionCodes={submissionCodes} />
            <ReceiptInstanceNumberField receiptSubmission={submission} />
            <ReceiptSubmissionDateField receiptSubmission={submission} />
          </>
        )}
        {submissionCodes.protocolNumber && (
          <>
            <Space>
              <Text>
                {t('submission.numero-protocollo', 'Numero protocollo:')}
              </Text>
              <Text strong>{submissionCodes.protocolNumber}</Text>
            </Space>
            <Space>
              <Text>{t('submission.data-protocollo', 'Data protocollo:')}</Text>
              <Text strong>
                <ISODate date={submission?.dataProtocollo} />
              </Text>
            </Space>
          </>
        )}
        {semanticFields.length > 0 &&
          semanticFields.map(field => (
            <Space key={field.tagSemantico?.id}>
              <Text>{field.tagSemantico?.titolo}: </Text>
              <Text strong>{field.valore as any}</Text>
            </Space>
          ))}
      </Flex>
      {payments.some(p =>
        p.oneri?.some(
          o =>
            o.tipoOnere?.tipoVincolo ===
            EnumTipoOnereDTOTipoVincolo.DA_PAGARE_DOPO_INVIO,
        ),
      ) && (
        <>
          <VerticalSpace size={24} />
          <Title level={3}>
            {t('submission-menu-item.pagamenti', 'Pagamenti')}
          </Title>
          <PaymentsTable payments={payments} submission={submission!} />
        </>
      )}
    </div>
  );
}
